import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CART_STORAGE_KEY,
  useAppDispatch,
  useAppState,
} from '@/context/appState';
import { Address, DeliveryMethod, Product, Recipient } from '@/context/types';
import { useSearchParams } from 'react-router-dom';
import { ORDER_STEPS } from '@/pages/Order/Order';
import {
  ErrorNotification,
  SuccessNotification,
} from '@/components/toast/toastMessages';
import { MODAL_KEY } from '@/constants';
import { DiscountAPI } from '@/helpers/api/DiscountAPI';
import { UUID } from 'crypto';
import { BoxLine, ILine } from './useCatalog';

export type LineItem = ILine &
  BoxLine & {
    contents?: LineItem[];
    mixType?: string;
    localId: UUID;
    quantity: number;
  };

// ============================================================================
// COMMERCIAL PATTERN: Global Fetch Locks
// Declared OUTSIDE the hook so they instantly sync across all component instances
// ============================================================================
let isFetchingDiscount = false;
let hasAttemptedDiscountFetch = false;

export default function useLocalCart() {
  const { order, freeShipping } = useAppState();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const checkShippingDiscount = useCallback(async () => {
    // 1. Instantly abort if another component is already fetching,
    // or if we already tried fetching this session.
    if (isFetchingDiscount || hasAttemptedDiscountFetch) return;

    // 2. Lock the gate
    isFetchingDiscount = true;

    try {
      const res = await DiscountAPI.getFreeShipping();

      if (res.success && res.discount) {
        dispatch({
          type: 'SET_SHIPPING_DISCOUNT',
          freeShipping: res.discount.minimumRequirement,
        });
      }
    } catch (error) {
      console.error('Could not fetch free shipping discount', error);
    } finally {
      // 3. Unlock the fetcher and permanently mark the attempt as completed
      // so it never runs again during this app session.
      isFetchingDiscount = false;
      hasAttemptedDiscountFetch = true;
    }
  }, [dispatch]);

  useEffect(() => {
    // 4. Clean, singular dependency check
    if (!freeShipping && !hasAttemptedDiscountFetch) {
      checkShippingDiscount();
    }
  }, [freeShipping, checkShippingDiscount]);
  // 1. Bulletproof Local Storage Sync
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(order));
    } catch (error: any) {
      console.warn('Failed to save cart to local storage:', error);

      // Only warn the user once if storage is blocked (e.g., Private Browsing)
      if (
        error?.name === 'QuotaExceededError' ||
        error?.name === 'SecurityError'
      ) {
        ErrorNotification({
          title: 'Storage Blocked',
          message:
            'You appear to be in Private Browsing mode. Your cart will not be saved if you leave this page.',
        });
      }
    }
  }, [order]);

  const fillBox = (box: Product, mix: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('box', box.id);
    newParams.set('mix', mix);
    newParams.set('step', ORDER_STEPS[2]);

    setSearchParams(newParams, { replace: true });
  };

  // 2. Skinny Action Dispatchers
  const addLineItem = useCallback(
    (newItem: LineItem) => {
      // We pass the raw intent to the reducer.
      // The reducer MUST handle the deduplication logic!
      dispatch({
        type: 'LOCAL_UPD_LINE_ADD',
        newItem,
      });
    },
    [dispatch],
  );

  // 2. Skinny Action Dispatchers
  const updateBoxContents = useCallback(
    (localId: UUID, contents: LineItem[]) => {
      // We pass the raw intent to the reducer.
      // The reducer MUST handle the deduplication logic!
      dispatch({
        type: 'LOCAL_UPD_BOX_LINE',
        contents,
        localId,
      });
    },
    [dispatch],
  );

  const updateLineItemQuantity = useCallback(
    (id: string, newQuantity: number) => {
      if (newQuantity <= 0) {
        dispatch({
          type: 'LOCAL_UPD_LINE_REMOVE',
          id,
        });
        return;
      }

      dispatch({
        type: 'LOCAL_UPD_LINE_QUANTITY',
        id,
        newQuantity,
      });
    },
    [dispatch],
  );

  const removeLineItem = useCallback(
    (id: string) => {
      dispatch({
        type: 'LOCAL_UPD_LINE_REMOVE',
        id,
      });
    },
    [dispatch],
  );

  const updateRecipient = useCallback(
    (recipient: Partial<Recipient>) => {
      dispatch({
        type: 'LOCAL_UPD_RECIPIENT',
        recipient,
      });
    },
    [dispatch],
  );

  const setOrderNote = useCallback(
    (note: string) => {
      dispatch({
        type: 'LOCAL_UPD_RECIPIENT',
        recipient: { instructions: note },
      });
    },
    [dispatch],
  );

  const clearCart = useCallback(() => {
    dispatch({
      type: 'LOCAL_CART_CLEAR',
    });

    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear local storage:', error);
    }
  }, [dispatch]);

  const cartDict = useMemo(() => {
    return order.lines.reduce(
      (acc, item) => {
        // 1. Add item to the data object
        acc.data[item.id] = item;

        // 2. Check for box completion logic
        if (item?.contents?.length === item.capacity) {
          acc.hasBox = true;
        }

        return acc;
      },
      { data: {}, hasBox: false } as {
        data: Record<string, LineItem>;
        hasBox: boolean;
      },
    );
  }, [order.lines]);

  const updateDeliveryMethod = useCallback(
    (method: DeliveryMethod) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('type', method);

      setSearchParams(newParams, { replace: true });

      dispatch({
        type: 'CHANGE_ORDER_TYPE',
        deliveryMethod: method,
      });
    },
    [dispatch],
  );

  const openLocationPopover = useCallback(
    (address: Recipient, rect: DOMRect) => {
      dispatch({
        type: 'OPEN_MODAL',
        modal: MODAL_KEY.LOCATION_POPOVER,
        modalData: {
          anchorRect: rect,
          currentAddress: address, // Pass the active address so they can edit it

          // This callback gets passed down through the Popover -> into useConfirmAddress -> into EditAddressModal
          onAddressConfirmed: async (newAddressData: any) => {
            updateRecipient(newAddressData);

            // Optional: Update user default address here (like in Delivery.tsx)
            // if (newAddressData.defaultDeliveryCheckbox) {
            //   await update({
            //     newAddress: newAddressData,
            //     currentAddress: null,
            //   }).catch(console.error);
            // }

            dispatch({ type: 'CLOSE_MODAL' });
          },
        },
      });
    },
    [dispatch],
  );

  const openDatePicker = useCallback(
    (rect: DOMRect) => {
      // 2. Open the modal and pass the coordinates + callback in the payload
      dispatch({
        type: 'OPEN_MODAL',
        modal: MODAL_KEY.DATE_PICKER,
        modalData: {
          anchorRect: rect,
          currentDate: order.pickupDate,
          onConfirm: (date: Date) => {
            dispatch({
              type: 'CHANGE_PICKUP_DATE',
              date,
            });
          },
        },
      });
    },
    [dispatch],
  );

  const lockCheckout = useMemo(() => {
    const invalidQuantity = order.lines.find(
      (p) => !p?.contents?.length && p.quantity < 2,
    );
    if (invalidQuantity && !cartDict.hasBox) return true;
    return false;
  }, [order.lines]);

  return {
    totalItems: order.lines.reduce((acc, curr) => acc + curr.quantity, 0),
    cartDict: cartDict.data,
    hasBundle: cartDict.hasBox,
    cart: order,
    lockCheckout,
    addLineItem,
    updateLineItemQuantity,
    updateDeliveryMethod,
    openDatePicker,
    openLocationPopover,
    removeLineItem,
    updateRecipient,
    setOrderNote,
    clearCart,
    fillBox,
    freeShipping,
    updateBoxContents,
  };
}
